// Open the booking panel OVER the current page (board: the panel opens beside
// «اليوم» with the list still usable) — `openBookingPanel(prefill)` from any
// screen, rendered by <BookingPanelHost/> wherever the host is mounted.
// The integrator mounts one host in the shell (SHARED-REQUESTS); the deep
// link `/booking/new?…` (bookingHref) works without it.

import { createElement, useSyncExternalStore } from 'react';
import { BookingPanel, type BookingPrefill } from './BookingPanel';
import { baghdadDateOf, wallClock } from '../lib/time';

interface HostState {
  open: boolean;
  prefill: BookingPrefill;
  /** Bumped per open so a re-open starts a fresh panel. */
  seq: number;
}

type Listener = () => void;
let state: HostState = { open: false, prefill: {}, seq: 0 };
const listeners = new Set<Listener>();
const emit = () => listeners.forEach((l) => l());

export const bookingPanelStore = {
  get: (): HostState => state,
  open(prefill: BookingPrefill = {}): void {
    state = { open: true, prefill, seq: state.seq + 1 };
    emit();
  },
  close(): void {
    if (!state.open) return;
    state = { ...state, open: false };
    emit();
  },
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /** Test hook. */
  reset(): void {
    state = { open: false, prefill: {}, seq: 0 };
    emit();
  },
};

/** Open «موعد جديد» over the current page, optionally prefilled. */
export function openBookingPanel(prefill: BookingPrefill = {}): void {
  bookingPanelStore.open(prefill);
}

export function BookingPanelHost() {
  const st = useSyncExternalStore(bookingPanelStore.subscribe, bookingPanelStore.get, bookingPanelStore.get);
  return createElement(BookingPanel, { key: st.seq, open: st.open, prefill: st.prefill, onClose: bookingPanelStore.close });
}

/** Query keys of the `/booking/new` deep link. */
export const BOOKING_PARAMS = {
  customer: 'customer',
  q: 'q',
  name: 'name',
  date: 'date',
  time: 'time',
  staff: 'staff_id',
  service: 'service_id',
  now: 'now',
  /** An instant (RFC 3339) — the empty-slot links of «اليوم» / «التقويم» send
   *  `?start_at=<iso>&staff_id=<id>`; read as the Baghdad day + wall clock. */
  startAt: 'start_at',
} as const;

/** `/booking/new?customer=…&date=YYYY-MM-DD&time=HH:MM…` — prefilled deep link. */
export function bookingHref(p: BookingPrefill & { customerId?: string | null } = {}): string {
  const qs = new URLSearchParams();
  if (p.customerId ?? p.customer?.id) qs.set(BOOKING_PARAMS.customer, (p.customerId ?? p.customer?.id) as string);
  if (p.query) qs.set(BOOKING_PARAMS.q, p.query);
  if (p.newName != null) qs.set(BOOKING_PARAMS.name, p.newName);
  if (p.date) qs.set(BOOKING_PARAMS.date, p.date);
  if (p.time) qs.set(BOOKING_PARAMS.time, p.time);
  if (p.staffId) qs.set(BOOKING_PARAMS.staff, p.staffId);
  if (p.serviceId) qs.set(BOOKING_PARAMS.service, p.serviceId);
  if (p.now) qs.set(BOOKING_PARAMS.now, '1');
  const s = qs.toString();
  return s ? `/booking/new?${s}` : '/booking/new';
}

/** Parse the deep link back into a prefill. */
export function prefillFromSearch(search: URLSearchParams): BookingPrefill {
  const rawDate = search.get(BOOKING_PARAMS.date);
  const rawTime = search.get(BOOKING_PARAMS.time);
  const start = instantOf(search.get(BOOKING_PARAMS.startAt));
  // Explicit `date=` / `time=` win; `start_at=` fills whichever is absent.
  const date = rawDate && /^\d{4}-\d{2}-\d{2}$/.test(rawDate) ? rawDate : start ? baghdadDateOf(start) : null;
  const time = rawTime && /^\d{2}:\d{2}$/.test(rawTime) ? rawTime : start ? wallClock(start) : null;
  return {
    customerId: search.get(BOOKING_PARAMS.customer),
    query: search.get(BOOKING_PARAMS.q) ?? undefined,
    newName: search.get(BOOKING_PARAMS.name) ?? undefined,
    date,
    time,
    staffId: search.get(BOOKING_PARAMS.staff),
    serviceId: search.get(BOOKING_PARAMS.service),
    now: search.get(BOOKING_PARAMS.now) === '1',
  };
}

/** An RFC 3339 instant with an explicit offset (or Z), else null. */
function instantOf(raw: string | null): Date | null {
  if (!raw || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/.test(raw)) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}
